import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  Circle,
  Sparkles,
  ArrowRight,
  X,
  Image as ImageIcon,
  Scissors,
  Users,
  CalendarDays,
  CreditCard,
  Mail,
} from "lucide-react";

// Step-key → icon. Kept here (not in the API response) so the backend stays UI-agnostic.
const STEP_ICONS = {
  logo: ImageIcon,
  services: Scissors,
  staff: Users,
  google_cal: CalendarDays,
  stripe: CreditCard,
  email_domain: Mail,
};

/**
 * Per-tenant onboarding checklist shown at the top of the Dashboard.
 * Auto-hides when:
 *   - every step is done, OR
 *   - the owner has dismissed it via the X button (server-side flag).
 *
 * Each step is a deterministic computation server-side — no manual ticking.
 * Owner clicks "Go" → navigates to the relevant admin page → completes that
 * step → next dashboard load shows the green tick.
 */
export default function OnboardingChecklist() {
  const [data, setData] = useState(null);
  const [dismissing, setDismissing] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/onboarding/status`);
      setData(res.data);
    } catch (e) {
      // Auth failure or 401 — owner not authenticated yet, just hide.
      setData(null);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const dismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      await axios.post(`${API}/onboarding/dismiss`, { dismissed: true });
      setData(prev => prev ? { ...prev, dismissed: true } : prev);
    } catch (e) {
      // Ignore — surface no error UI; checklist will reappear on next load
    } finally {
      setDismissing(false);
    }
  };

  // Hide entirely when not loaded yet, dismissed, or 100% complete.
  if (!data) return null;
  if (data.dismissed) return null;
  if (data.all_done) return null;

  const { steps, completed_count, total, percent } = data;
  const remaining = total - completed_count;

  return (
    <Card
      data-testid="onboarding-checklist"
      className="p-5 rounded-2xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-background to-background overflow-hidden relative"
    >
      <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-primary/10 blur-2xl" />
      <div className="relative">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
              <Sparkles size={17} className="text-primary" />
            </div>
            <div>
              <h2
                className="text-lg font-semibold tracking-tight"
                style={{ fontFamily: 'Playfair Display' }}
                data-testid="onboarding-title"
              >
                Get your salon ready
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5" data-testid="onboarding-progress-label">
                <span className="font-semibold text-foreground">{completed_count} of {total}</span>{' '}
                complete &middot; {remaining} {remaining === 1 ? 'step' : 'steps'} left
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            data-testid="onboarding-dismiss-button"
            disabled={dismissing}
            className="text-muted-foreground hover:text-foreground transition-colors shrink-0 rounded-lg p-1 hover:bg-muted/60 disabled:opacity-50"
            aria-label="Hide onboarding checklist"
          >
            <X size={16} />
          </button>
        </div>

        {/* Progress bar */}
        <div className="mb-5">
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div
              data-testid="onboarding-progress-bar"
              className="h-full bg-gradient-to-r from-primary to-primary/80 rounded-full transition-all duration-500"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>

        {/* Steps */}
        <ul className="space-y-2">
          {steps.map(step => {
            const Icon = STEP_ICONS[step.key] || Circle;
            return (
              <li key={step.key}>
                <div
                  data-testid={`onboarding-step-${step.key}`}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                    step.done
                      ? 'border-green-200 bg-green-50/40'
                      : 'border-border bg-card hover:border-primary/30 hover:bg-muted/30'
                  }`}
                >
                  {/* Done state vs not-done */}
                  {step.done ? (
                    <CheckCircle2 size={20} className="text-green-600 shrink-0" />
                  ) : (
                    <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <Icon size={16} className="text-muted-foreground" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium leading-tight ${step.done ? 'text-green-900 line-through decoration-green-600/40' : ''}`}>
                      {step.label}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                      {step.done ? step.hint : step.description}
                    </p>
                  </div>

                  {!step.done && (
                    <Link to={step.deeplink} data-testid={`onboarding-step-${step.key}-cta`}>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-xl text-xs whitespace-nowrap"
                      >
                        Go <ArrowRight size={12} className="ml-1" />
                      </Button>
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </Card>
  );
}
